import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode } from 'react'
import Alert, { AlertProps } from './index'

interface NotificationItem extends Omit<AlertProps, 'onClose'> {
  id: string
}

interface AlertContextType {
  showAlert: (props: Omit<AlertProps, 'onClose'>) => void
  showInfo: (message: string, options?: Partial<AlertProps>) => void
  showSuccess: (message: string, options?: Partial<AlertProps>) => void
  showWarning: (message: string, options?: Partial<AlertProps>) => void
  showError: (message: string, options?: Partial<AlertProps>) => void
}

const AlertContext = createContext<AlertContextType | null>(null)

interface AlertProviderProps {
  children: ReactNode
  maxAlerts?: number
}

export const AlertProvider: React.FC<AlertProviderProps> = ({
  children,
  maxAlerts = 3
}) => {
  const [notifications, setNotifications] = useState<NotificationItem[]>([])

  // 콜백 identity를 고정해 컨텍스트 소비자의 effect 의존성이 매 렌더마다 바뀌지 않도록 한다.
  // (알림이 뜨거나 자동으로 닫힐 때마다 Provider가 리렌더되는데, 메모이즈하지 않으면
  //  show* 함수가 매번 새 참조가 되어 이를 deps에 넣은 effect가 불필요하게 재실행됨)
  const removeNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(notif => notif.id !== id))
  }, [])

  const showAlert = useCallback((props: Omit<AlertProps, 'onClose'>) => {
    const timestamp = Date.now()
    const id = timestamp.toString() + '.' + Math.random().toString(36).substr(2, 9)
    const newNotification: NotificationItem = {
      ...props,
      id,
      autoClose: props.autoClose ?? true,
      duration: props.duration ?? 5000
    }

    setNotifications(prev => {
      // Check for duplicate messages within the last 1 second
      const duplicateExists = prev.some(notif => {
        const notifTimestamp = parseInt(notif.id.split('.')[0] || '0')
        return notif.message === newNotification.message &&
               notif.variant === newNotification.variant &&
               (timestamp - notifTimestamp) < 1000
      })

      if (duplicateExists) {
        console.log('Duplicate alert prevented:', newNotification.message)
        return prev
      }

      const updated = [newNotification, ...prev].slice(0, maxAlerts)
      return updated
    })

    // Auto-remove if autoClose is enabled
    if (newNotification.autoClose) {
      setTimeout(() => {
        removeNotification(id)
      }, newNotification.duration)
    }
  }, [maxAlerts, removeNotification])

  const showInfo = useCallback((message: string, options: Partial<AlertProps> = {}) => {
    showAlert({ ...options, message, variant: 'info' })
  }, [showAlert])

  const showSuccess = useCallback((message: string, options: Partial<AlertProps> = {}) => {
    showAlert({ ...options, message, variant: 'success' })
  }, [showAlert])

  const showWarning = useCallback((message: string, options: Partial<AlertProps> = {}) => {
    showAlert({ ...options, message, variant: 'warning' })
  }, [showAlert])

  const showError = useCallback((message: string, options: Partial<AlertProps> = {}) => {
    showAlert({ ...options, message, variant: 'error' })
  }, [showAlert])

  const contextValue = useMemo(
    () => ({ showAlert, showInfo, showSuccess, showWarning, showError }),
    [showAlert, showInfo, showSuccess, showWarning, showError]
  )

  return (
    <AlertContext.Provider value={contextValue}>
      {children}
      
      {/* Global Alert Container */}
      <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 space-y-2">
        {notifications.map((notification, index) => (
          <div
            key={notification.id}
            className="animate-in slide-in-from-top-2 fade-in duration-300"
            style={{
              animationDelay: `${index * 100}ms`
            }}
          >
            <Alert
              {...notification}
              onClose={() => removeNotification(notification.id)}
            />
          </div>
        ))}
      </div>
    </AlertContext.Provider>
  )
}

export const useAlert = () => {
  const context = useContext(AlertContext)
  if (!context) {
    throw new Error('useAlert must be used within an AlertProvider')
  }
  return context
}